import React, { useState } from 'react';
import { Printer, X, FileText, CheckCircle2, AlertTriangle, Upload, Clock, RefreshCw, Search } from 'lucide-react';
import { ClinicalDocument } from '../types';
import { playPulseSeal } from '../utils/pulseAudio';

const EMPTY_DOCS: ClinicalDocument[] = [];

interface ClientFaxModalProps {
  clientId?: string;
  clientName?: string;
  recipientFax?: string;
  recipientName?: string;
  clientDocuments?: ClinicalDocument[];
  onClose: () => void;
}

export const ClientFaxModal: React.FC<ClientFaxModalProps> = ({
  clientId: initialClientId = '',
  clientName: initialClientName = '',
  recipientFax: initialRecipientFax = '',
  recipientName: initialRecipientName = '',
  clientDocuments = EMPTY_DOCS,
  onClose
}) => {
  const [clientId, setClientId] = useState(initialClientId);
  const [clientName, setClientName] = useState(initialClientName);
  const [docs, setDocs] = useState<ClinicalDocument[]>(clientId ? clientDocuments : []);
  const [recipientFax, setRecipientFax] = useState(initialRecipientFax);
  const [recipientName, setRecipientName] = useState(initialRecipientName);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [upload, setUpload] = useState<{ name: string; content: string } | null>(null);
  const [includeCover, setIncludeCover] = useState(false);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [faxResult, setFaxResult] = useState<any | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [previewInfo, setPreviewInfo] = useState<{ fileName: string; pageCount: number; coverPageCount: number; byteLength: number } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientChoices, setClientChoices] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  React.useEffect(() => {
    if (!clientId) {
      setDocs([]);
      return;
    }
    if (clientDocuments.length > 0 && clientId === initialClientId) {
      setDocs(clientDocuments);
      return;
    }
    fetch(`/api/clinical/docs?client_id=${encodeURIComponent(clientId)}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setDocs(data); })
      .catch(() => {});
  }, [clientId, clientDocuments, initialClientId]);

  React.useEffect(() => {
    if (!showClientSearch || clientChoices.length > 0) return;
    setLoadingClients(true);
    fetch('/api/clients')
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setClientChoices(data); })
      .catch(() => {})
      .finally(() => setLoadingClients(false));
  }, [showClientSearch, clientChoices.length]);

  React.useEffect(() => {
    const hasUpload = Boolean(upload?.content);
    const hasDoc = Boolean(selectedDocId);
    if (!hasUpload && !hasDoc) {
      setPreviewInfo(null);
      setPreviewError(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    fetch('/api/communications/fax/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        document_id: hasUpload ? null : selectedDocId,
        file_content: hasUpload ? upload?.content : undefined,
        file_name: hasUpload ? upload?.name : undefined,
        recipient_name: recipientName,
        recipient_fax: recipientFax,
        notes,
        client_id: clientId || null,
        include_cover: includeCover
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setPreviewInfo({
            fileName: data.fileName,
            pageCount: data.pageCount,
            coverPageCount: data.coverPageCount || 0,
            byteLength: data.byteLength
          });
          setPreviewError(null);
        } else {
          setPreviewInfo(null);
          setPreviewError(data.error || 'The selected file could not be prepared as a PDF.');
        }
      })
      .catch(() => {
        setPreviewInfo(null);
        setPreviewError('The selected file could not be prepared as a PDF.');
      })
      .finally(() => setPreviewLoading(false));
  }, [selectedDocId, upload, includeCover, recipientName, recipientFax, notes, clientId]);

  const handleCheckStatus = async () => {
    if (!faxResult?.faxId) return;
    try {
      setCheckingStatus(true);
      const res = await fetch(`/api/communications/faxes/${faxResult.faxId}/sync`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.fax) {
          setFaxResult((prev: any) => prev ? {
            ...prev,
            status: data.fax.status,
            failureReason: data.fax.failure_reason,
            message: data.fax.status === 'DELIVERED'
              ? 'Faxage confirmed delivery.'
              : data.fax.status === 'PENDING'
                ? 'Faxage accepted the fax. Delivery is not confirmed.'
                : (data.fax.failure_reason || prev.message)
          } : null);
        }
      }
    } catch (e) {
      console.error('Failed to sync fax status:', e);
    } finally {
      setCheckingStatus(false);
    }
  };

  React.useEffect(() => {
    if (!faxResult || faxResult.status !== 'PENDING' || !faxResult.faxId) return;
    const interval = setInterval(() => { handleCheckStatus(); }, 8000);
    return () => clearInterval(interval);
  }, [faxResult?.status, faxResult?.faxId]);

  const selectedDoc = docs.find(d => d.id === selectedDocId);
  const filteredClients = clientChoices.filter((c) => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return name.includes(q) || String(c.id || '').toLowerCase().includes(q);
  }).slice(0, 20);

  const chooseClient = (client: any) => {
    setClientId(String(client.id));
    setClientName(`${client.first_name || ''} ${client.last_name || ''}`.trim());
    setSelectedDocId('');
    setShowClientSearch(false);
    setClientSearch('');
    if (!recipientFax && client.physician_fax) setRecipientFax(client.physician_fax);
    if (!recipientName && client.physician_name) setRecipientName(client.physician_name);
  };

  const clearClient = () => {
    setClientId('');
    setClientName('');
    setDocs([]);
    setSelectedDocId('');
    setShowClientSearch(true);
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = typeof reader.result === 'string' ? reader.result : '';
      setUpload({ name: file.name, content });
      setSelectedDocId('');
      setPreviewError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleSendFax = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientFax || (!upload && !selectedDocId)) return;

    const docName = upload
      ? upload.name
      : selectedDoc
        ? `${selectedDoc.doc_type || 'Document'} - ${selectedDoc.title || selectedDoc.id}`
        : 'Uploaded PDF';

    try {
      setSending(true);
      setFaxResult(null);
      const res = await fetch('/api/communications/fax/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId || null,
          recipient_fax: recipientFax,
          recipient_name: recipientName,
          document_id: upload ? null : selectedDocId || null,
          file_content: upload?.content,
          file_name: upload?.name,
          document_name: docName,
          notes,
          include_cover: includeCover
        })
      });
      const data = await res.json();
      if (res.ok && data.success) playPulseSeal();
      setFaxResult(data);
    } catch (err: any) {
      setFaxResult({
        success: false,
        status: 'FAILED',
        failureCode: 'FAX_TRANSMISSION',
        failureReason: err.message || 'Network error attempting outbound fax delivery.'
      });
    } finally {
      setSending(false);
    }
  };

  const resultTone = faxResult?.status === 'DELIVERED'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : faxResult?.status === 'FAILED'
      ? 'bg-rose-50 border-rose-200 text-rose-800'
      : 'bg-amber-50 border-amber-200 text-amber-800';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative max-w-full sm:max-w-lg w-full h-full sm:h-auto rounded-none sm:rounded-2xl bg-white border-0 sm:border border-slate-200/90 shadow-xl overflow-hidden flex flex-col">
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center shrink-0">
          <div className="flex items-center space-x-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 shrink-0">
              <Printer className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-white tracking-tight">Electronic Fax</h2>
              <p className="text-[11px] text-slate-300 truncate">{clientName || 'No client selected'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 text-xs flex-1 overflow-y-auto pb-[calc(20px+env(safe-area-inset-bottom,0px))] sm:pb-5">
          <form onSubmit={handleSendFax} className="space-y-3.5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Client</label>
                {clientId ? (
                  <button type="button" onClick={clearClient} className="text-[10px] text-slate-500 underline">Clear client</button>
                ) : null}
              </div>
              {clientId ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-800">{clientName || clientId}</div>
              ) : (
                <p className="text-[11px] text-slate-500">Optional. Choose a client only when you want that chart's documents.</p>
              )}
              <button
                type="button"
                onClick={() => setShowClientSearch(open => !open)}
                className="mt-1 text-[10px] text-slate-500 underline"
              >
                {showClientSearch ? 'Hide client search' : 'Find client'}
              </button>
              {showClientSearch && (
                <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Search by client name..."
                      className="w-full bg-white border-b border-slate-200 pl-8 pr-3 py-2 text-xs focus:outline-none"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    {loadingClients && filteredClients.length === 0 ? (
                      <div className="px-3 py-2 text-slate-500">Loading clients...</div>
                    ) : filteredClients.length === 0 ? (
                      <div className="px-3 py-2 text-slate-500">No matching clients</div>
                    ) : filteredClients.map(client => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => chooseClient(client)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        {client.last_name}, {client.first_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Recipient / Facility Name</label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Office or medical records"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-hidden focus:ring-1 focus:ring-sky-500"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Recipient Fax Number</label>
              <input
                type="tel"
                value={recipientFax}
                onChange={(e) => setRecipientFax(e.target.value)}
                placeholder="(480) 555-0199"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 font-mono focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                required
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">PDF from this computer</label>
              <label className="flex items-center gap-2 w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 text-slate-700 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                <span>{upload ? upload.name : 'Choose a PDF'}</span>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Or an existing chart document</label>
              <select
                value={selectedDocId}
                onChange={(e) => {
                  setSelectedDocId(e.target.value);
                  if (e.target.value) setUpload(null);
                }}
                className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-hidden focus:ring-1 focus:ring-sky-500"
              >
                <option value="">{clientId ? 'No document selected' : 'Select a client to list chart documents'}</option>
                {docs.map((d) => (
                  <option key={d.id} value={d.id}>
                    [{d.doc_type}] {d.title || d.id}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-[11px] text-slate-700">
              <input
                type="checkbox"
                checked={includeCover}
                onChange={(e) => setIncludeCover(e.target.checked)}
              />
              Include a cover page
            </label>

            {previewLoading && (
              <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-500 flex items-center space-x-2">
                <RefreshCw className="w-3 h-3 animate-spin text-slate-600" />
                <span>Checking the PDF...</span>
              </div>
            )}

            {previewInfo && !previewLoading && (
              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-700 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800 truncate max-w-[280px]">{previewInfo.fileName}</p>
                    <p className="text-[10px] text-slate-500">
                      {previewInfo.pageCount} page{previewInfo.pageCount === 1 ? '' : 's'}
                      {previewInfo.coverPageCount ? ' including a cover page' : ''}
                      {' '}• PDF ({Math.round(previewInfo.byteLength / 1024)} KB)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {previewError && (
              <div className="p-2.5 rounded-lg border border-rose-200 bg-rose-50 text-[11px] text-rose-800">{previewError}</div>
            )}

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Cover note</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional note. It is printed only when the cover page is included."
                rows={2}
                className="w-full bg-slate-50/70 border border-slate-200 rounded-lg p-2 text-base sm:text-xs text-slate-800 focus:outline-hidden focus:ring-1 focus:ring-sky-500"
              />
            </div>

            {faxResult && (
              <div className={`p-3.5 rounded-xl border text-xs space-y-1.5 ${resultTone}`}>
                <div className="flex items-center justify-between font-bold">
                  <div className="flex items-center space-x-2">
                    {faxResult.status === 'DELIVERED' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>Faxage confirmed delivery</span>
                      </>
                    ) : faxResult.status === 'PENDING' ? (
                      <>
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span>Faxage accepted the fax. Delivery is not confirmed.</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                        <span>Fax was not sent</span>
                      </>
                    )}
                  </div>
                  {faxResult.status === 'PENDING' && (
                    <button
                      type="button"
                      onClick={handleCheckStatus}
                      disabled={checkingStatus}
                      className="px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 text-[10px] flex items-center space-x-1"
                    >
                      <RefreshCw className={`w-3 h-3 ${checkingStatus ? 'animate-spin' : ''}`} />
                      <span>{checkingStatus ? 'Checking...' : 'Check Status'}</span>
                    </button>
                  )}
                </div>
                <p className="text-[11px] leading-relaxed">{faxResult.failureReason || faxResult.message}</p>
                {faxResult.providerReferenceId && (
                  <div className="p-2 bg-white/70 rounded border border-slate-200 text-[10px] font-mono">
                    Faxage job {faxResult.providerReferenceId}
                  </div>
                )}
                {faxResult.status === 'FAILED' && faxResult.failureCode === 'FAX_CONFIGURATION' && (
                  <div className="p-2 bg-white/70 rounded border border-slate-200 text-[10px] font-mono">
                    Required configuration: FAXAGE_COMPANY, FAXAGE_USERNAME, and FAXAGE_PASSWORD. This is separate from a document PDF problem.
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={sending || !recipientFax || (!upload && !selectedDocId)}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-xs cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span>{sending ? 'Transmitting Fax...' : 'Send Outbound Fax'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
